import React from 'react';
import { Subscription, isBrowser, ORIGINAL_URL_COOKIE_ENDPOINT } from '@packages/common';
import { ClickoutContext, WishlistContext } from '@packages/shared';

const MAX_STEP = 5; // 6 steps (of 5 min)
const STEP_TIMEOUT = 5 * 60 * 1000; // 5 min
// settle events in subscriptions timeout
const INITIAL_TIMEOUT = 10000;

export async function updateOriginalUrlCookie(): Promise<Response | undefined> {
  if (!isBrowser()) {
    return;
  }
  return await fetch(`${ORIGINAL_URL_COOKIE_ENDPOINT}?cos_path=${encodeURIComponent(location.href)}`, {
    headers: {
      'Content-Type': 'text/javascript',
    },
  });
}

export const OriginalUrlCookieRenewer: React.FC<{ waitForInitialLoad?: boolean }> = ({
  children,
  waitForInitialLoad = true,
}) => {
  const { clickoutSubject } = React.useContext(ClickoutContext);
  const { wishlistSubject } = React.useContext(WishlistContext);
  // Logic: cookie should be renewed on any interaction within 30 min.
  // To avoid detecting every click, we just detect clicks once in 5 minutes step
  // If user has page open, but does not interact with it - cookie should be gone in 30 min
  const step = React.useRef(0);
  const wasClicked = React.useRef(false);
  const isInitialLoad = React.useRef(waitForInitialLoad);
  const clickoutSubscriptionRef = React.useRef<Subscription | null>(null);
  const wishlistSubscriptionRef = React.useRef<Subscription | null>(null);
  const listenerRef = React.useRef<VoidFunction | null>(null);

  const unsubscribeAll = React.useCallback((listenFunc: VoidFunction) => {
    window.removeEventListener('click', listenFunc);
    clickoutSubscriptionRef?.current?.unsubscribe();
    wishlistSubscriptionRef?.current?.unsubscribe();
    clickoutSubscriptionRef.current = null;
    wishlistSubscriptionRef.current = null;
  }, []);

  const subscribeAll = React.useCallback(
    (listenFunc: VoidFunction) => {
      unsubscribeAll(listenFunc);
      window.addEventListener('click', listenFunc);

      clickoutSubscriptionRef.current = clickoutSubject.subscribe(listenFunc);
      wishlistSubscriptionRef.current = wishlistSubject.subscribe(listenFunc);
    },
    [clickoutSubject, unsubscribeAll, wishlistSubject],
  );

  const listener =
    listenerRef.current ||
    function listener() {
      if (isInitialLoad.current || wasClicked.current) {
        return;
      }
      unsubscribeAll(listener);
      wasClicked.current = true;
    };
  listenerRef.current = listener;

  // Initialization and initial cookie request
  React.useEffect(() => {
    const initialRemoveTimeout = setTimeout(() => {
      isInitialLoad.current = false;
    }, INITIAL_TIMEOUT);
    updateOriginalUrlCookie();
    subscribeAll(listener);
    return () => {
      clearTimeout(initialRemoveTimeout);
    };
  });

  // On click side effects
  React.useEffect(() => {
    const stepInterval = setInterval(() => {
      if (step.current >= MAX_STEP) {
        // Do not renew cookie after 30 min of inactivity
        clearInterval(stepInterval);
        unsubscribeAll(listener);
        return;
      }
      step.current++;
      if (wasClicked.current) {
        wasClicked.current = false;
        step.current = 0;
        updateOriginalUrlCookie();
        subscribeAll(listener);
      }
    }, STEP_TIMEOUT);
    return () => {
      unsubscribeAll(listener);
      clearInterval(stepInterval);
    };
  });

  return <>{children}</>;
};
